import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { toast } from '../../utils/toast.js';
import { TextInput } from '../../components/Inputs/TextInput.js';
import { Button } from '../../components/Buttons/Button.js';
import { logger } from '@digiwedge/logger';
import { getMessage } from '../../i18n/messages.js';
const FORM_FIELD_KEYS = [
    'email',
    'password',
    'givenName',
    'familyName',
    'preferredName',
    'policyProfile',
    'termsAccepted',
    'confirmPassword',
];
const isFormFieldKey = (value) => FORM_FIELD_KEYS.includes(value);
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const getPasswordValidationError = (password, mode) => {
    if (!password) {
        return mode === 'submit' ? getMessage('en-US', 'registerAccountPasswordRequired') : undefined;
    }
    if (password.length < 8) {
        return getMessage('en-US', 'registerAccountPasswordMin');
    }
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    if (!hasUpper || !hasLower || !hasNumber) {
        return getMessage('en-US', 'registerAccountPasswordComplex');
    }
    return undefined;
};
export const StepAccountForm = ({ onNext, loading, serverError, className, initialValues, }) => {
    const createInitialFormState = (values) => ({
        email: values?.email ?? '',
        password: '',
        givenName: values?.givenName ?? '',
        familyName: values?.familyName ?? '',
        preferredName: values?.preferredName ?? '',
    });
    const [formData, setFormData] = useState(() => createInitialFormState(initialValues));
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const serverFieldErrors = serverError?.fieldErrors;
    const hasServerFieldErrors = Boolean(serverFieldErrors && Object.keys(serverFieldErrors).length > 0);
    const hasRecoveryActions = Boolean(serverError?.recoveryActions?.length);
    const blocksPasswordRegistration = Boolean(serverError?.recoveryActions?.length);
    const disablePasswordRegistrationFields = blocksPasswordRegistration;
    const accountFormIsValid = isValidEmail(formData.email) &&
        !getPasswordValidationError(formData.password, 'submit') &&
        confirmPassword === formData.password &&
        Boolean(confirmPassword) &&
        Boolean(formData.givenName.trim()) &&
        Boolean(formData.familyName.trim());
    const submitDisabled = loading || blocksPasswordRegistration || !accountFormIsValid;
    const shouldShowServerMessage = Boolean(serverError?.message && (!hasServerFieldErrors || hasRecoveryActions));
    useEffect(() => {
        if (serverError?.fieldErrors) {
            const sanitizedErrors = Object.entries(serverError.fieldErrors).reduce((acc, [key, value]) => {
                if (isFormFieldKey(key)) {
                    acc[key] = value;
                }
                return acc;
            }, {});
            setErrors((prev) => ({ ...prev, ...sanitizedErrors }));
        }
    }, [serverError]);
    useEffect(() => {
        setFormData(createInitialFormState(initialValues));
        setConfirmPassword('');
        setErrors({});
    }, [initialValues]);
    const handleChange = (field) => (value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (field === 'password') {
            setErrors((prev) => {
                const updated = {
                    ...prev,
                    password: getPasswordValidationError(value, 'change'),
                };
                if (confirmPassword && confirmPassword !== value) {
                    updated.confirmPassword = getMessage('en-US', 'registerAccountConfirmPasswordMismatch');
                }
                else {
                    updated.confirmPassword = undefined;
                }
                return updated;
            });
            return;
        }
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };
    const handleConfirmPasswordChange = (value) => {
        setConfirmPassword(value);
        setErrors((prev) => {
            const updated = {
                ...prev,
                confirmPassword: undefined,
            };
            if (formData.password && value && formData.password !== value) {
                updated.confirmPassword = getMessage('en-US', 'registerAccountConfirmPasswordMismatch');
            }
            return updated;
        });
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (blocksPasswordRegistration) {
            return;
        }
        const newErrors = {};
        if (!formData.email) {
            newErrors['email'] = getMessage('en-US', 'registerAccountEmailRequired');
        }
        else if (!isValidEmail(formData.email)) {
            newErrors['email'] = getMessage('en-US', 'registerAccountInvalidEmail');
        }
        const passwordError = getPasswordValidationError(formData.password, 'submit');
        if (passwordError) {
            newErrors['password'] = passwordError;
        }
        if (!formData.givenName.trim()) {
            newErrors['givenName'] = getMessage('en-US', 'registerAccountFirstNameRequired');
        }
        if (!formData.familyName.trim()) {
            newErrors['familyName'] = getMessage('en-US', 'registerAccountLastNameRequired');
        }
        if (!confirmPassword) {
            newErrors['confirmPassword'] = getMessage('en-US', 'registerAccountConfirmPasswordRequired');
        }
        else if (formData.password !== confirmPassword) {
            newErrors['confirmPassword'] = getMessage('en-US', 'registerAccountConfirmPasswordMismatch');
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            logger.warn('Validation Failed:', { errors: newErrors });
            if (process.env['NODE_ENV'] === 'development') {
                console.warn('Validation Failed:', newErrors);
            }
            return;
        }
        try {
            const payload = { ...formData };
            await onNext(payload);
        }
        catch {
            toast.error(getMessage('en-US', 'registrationSubmitError'));
        }
    };
    return (_jsx("div", { className: className, children: _jsxs("form", { className: "space-y-4", onSubmit: handleSubmit, noValidate: true, children: [shouldShowServerMessage && (_jsxs("section", { className: "rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 shadow-sm", role: "alert", children: [serverError?.title ? _jsx("p", { className: "font-semibold", children: serverError.title }) : null, _jsx("p", { className: serverError?.title ? 'mt-1 text-amber-900' : undefined, children: serverError?.message }), hasRecoveryActions ? (_jsx("p", { className: "mt-2 text-amber-800", children: getMessage('en-US', 'registrationIntentNextStep') })) : null, serverError?.recoveryActions?.length ? (_jsx("div", { className: "mt-3 flex flex-col gap-2 sm:flex-row", children: serverError.recoveryActions.map((action) => (_jsxs("a", { href: action.href, className: "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-3 py-2 font-medium text-amber-950 transition hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500", children: [action.label, _jsx(ArrowRightIcon, { className: "h-4 w-4", "aria-hidden": "true" })] }, action.href))) })) : null] })), _jsxs("div", { children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block", children: "Email" }), _jsx(TextInput, { type: "email", value: formData.email, onChange: handleChange('email'), className: errors['email'] ? 'border-red-500' : '', inputProps: {
                                        'aria-describedby': undefined,
                                    } })] }), errors['email'] && _jsx("p", { className: "text-red-500 text-sm mt-1", children: errors['email'] })] }), _jsxs("div", { children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block", children: "Password" }), _jsx(TextInput, { type: showPassword ? 'text' : 'password', value: formData.password, onChange: handleChange('password'), showClearButton: false, inputProps: { disabled: disablePasswordRegistrationFields }, rightAdornment: _jsxs("button", { type: "button", onClick: () => setShowPassword((prev) => !prev), className: "rounded-full p-2 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary", "aria-label": showPassword ? 'Hide password' : 'Show password', "aria-pressed": showPassword, title: showPassword ? 'Hide password' : 'Show password', children: [showPassword ? (_jsx(EyeSlashIcon, { className: "h-5 w-5", "aria-hidden": "true" })) : (_jsx(EyeIcon, { className: "h-5 w-5", "aria-hidden": "true" })), _jsx("span", { className: "sr-only", children: showPassword ? 'Hide password' : 'Show password' })] }), className: errors['password'] ? 'border-red-500' : '' })] }), errors['password'] && _jsx("p", { className: "text-red-500 text-sm mt-1", children: errors['password'] })] }), _jsxs("div", { children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block", children: getMessage('en-US', 'registerAccountConfirmPasswordLabel') }), _jsx(TextInput, { type: showConfirmPassword ? 'text' : 'password', value: confirmPassword, onChange: handleConfirmPasswordChange, showClearButton: false, inputProps: { disabled: disablePasswordRegistrationFields }, rightAdornment: _jsxs("button", { type: "button", onClick: () => setShowConfirmPassword((prev) => !prev), className: "rounded-full p-2 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary", "aria-label": showConfirmPassword ? 'Hide password' : 'Show password', "aria-pressed": showConfirmPassword, title: showConfirmPassword ? 'Hide password' : 'Show password', children: [showConfirmPassword ? (_jsx(EyeSlashIcon, { className: "h-5 w-5", "aria-hidden": "true" })) : (_jsx(EyeIcon, { className: "h-5 w-5", "aria-hidden": "true" })), _jsx("span", { className: "sr-only", children: showConfirmPassword ? 'Hide password' : 'Show password' })] }), className: errors['confirmPassword'] ? 'border-red-500' : '' })] }), errors['confirmPassword'] && (_jsx("p", { className: "text-red-500 text-sm mt-1", children: errors['confirmPassword'] }))] }), _jsxs("div", { children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block", children: "First Name" }), _jsx(TextInput, { value: formData.givenName, onChange: handleChange('givenName'), className: errors['givenName'] ? 'border-red-500' : '', inputProps: { disabled: disablePasswordRegistrationFields } })] }), errors['givenName'] && (_jsx("p", { className: "text-red-500 text-sm mt-1", children: errors['givenName'] }))] }), _jsxs("div", { children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block", children: "Last Name" }), _jsx(TextInput, { value: formData.familyName, onChange: handleChange('familyName'), className: errors['familyName'] ? 'border-red-500' : '', inputProps: { disabled: disablePasswordRegistrationFields } })] }), errors['familyName'] && (_jsx("p", { className: "text-red-500 text-sm mt-1", children: errors['familyName'] }))] }), _jsx("div", { children: _jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block", children: "Preferred Name" }), _jsx(TextInput, { value: formData.preferredName, onChange: handleChange('preferredName'), inputProps: { disabled: disablePasswordRegistrationFields } })] }) }), _jsx(Button, { htmlType: "submit", fullWidth: true, disabled: submitDisabled, "aria-busy": loading, children: getMessage('en-US', 'registerButton') })] }) }));
};
//# sourceMappingURL=StepAccountForm.js.map